import { CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material'
import { Delete as DeleteIcon, Autorenew as ReprocessIcon } from '@mui/icons-material'

import { KnowledgeBaseEmbeddedDocument, ProcessingStatus } from '@/core/models/knowledge-base-collection.model'
import StatusBadge from './StatusBadge'
import { format } from 'date-fns'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

type DocumentsTableProps = {
  documents: KnowledgeBaseEmbeddedDocument[]
  isLoading: boolean
  isProcessing: boolean
  onDelete: (document: KnowledgeBaseEmbeddedDocument) => void
  onReprocess: (documentKey: string) => void
}

function DocumentsTable({ documents, isLoading, isProcessing, onDelete, onReprocess }: DocumentsTableProps) {
  useLingui()
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Trans>Title</Trans>
            </TableCell>
            <TableCell>
              <Trans>Status</Trans>
            </TableCell>
            <TableCell>
              <Trans>Pages</Trans>
            </TableCell>
            <TableCell>
              <Trans>Chunks</Trans>
            </TableCell>
            <TableCell>
              <Trans>Processed At</Trans>
            </TableCell>
            <TableCell align="right">
              <Trans>Actions</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : documents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Trans>No documents in this collection yet.</Trans>
              </TableCell>
            </TableRow>
          ) : (
            documents.map((doc) => (
              <TableRow key={doc.key}>
                <TableCell>{doc.title}</TableCell>
                <TableCell>
                  <StatusBadge status={doc.status} />
                  {doc.status === ProcessingStatus.FAILED && (
                    <Tooltip title={doc.processingError}>
                      <small style={{ color: 'red', marginLeft: '8px' }}>Error</small>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{doc.pageCount ?? 'N/A'}</TableCell>
                <TableCell>{doc.chunkCount ?? 'N/A'}</TableCell>
                <TableCell>{doc.processedAt ? format(new Date(doc.processedAt), 'Pp') : 'N/A'}</TableCell>
                <TableCell align="right">
                  {doc.status !== ProcessingStatus.PROCESSING && (
                    <Tooltip title={t`Reprocess document`}>
                      <IconButton onClick={() => onReprocess(doc.key)} disabled={isProcessing}>
                        <ReprocessIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t`Delete document`}>
                    <IconButton onClick={() => onDelete(doc)} disabled={isProcessing}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default DocumentsTable
